import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useSearch } from '../Keyvault/SearchContext';
import { useNavigate } from 'react-router-dom';
import { getEnvironmentDetails } from '../../apis/tenantDetailsApi/tenantDetailsApi';
import { checkRolePermissions } from '../../utils/HasPermission';

interface EnvItem {
   Id: string;                       
  Env_Nm: string;                   
  Env_Desc: string;    
  Env_Typ:string;              
  EnvDisplay_Nm: string;             
  EnvSrvc_Conn: string;              
  EnvSrvc_Loc: string;               
  EnvRg_Nm: string;                   
  EnvBase_Nm: string;                
  EnvKvlt_Id: string;  
  Keyvault_Nm: string;               
  EnvSubscrptn_Id: string;             
  EnvStorAcct_Nm: string;     
  EnvCont_Nm: string;          
  EffectiveStart_DtTm: string;       
  EffectiveEnd_DtTm: string;          
  Active_Flg: string;                  
  CreatedBy_Id: string;               
  CreatedBy_DtTm: string;            
  UpdatedBy_Id: string;  
   UpdatedBy_DtTm:string;              
}

const EnvList: React.FC = () => {
  const navigate = useNavigate();
  const { searchText } = useSearch();
  const [envs, setEnvs] = useState<EnvItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const isReadEnabled = checkRolePermissions("Environment", false, "read")

 const loadData = async () => {
  setLoading(true);
  setError(null);
  try {
    const response = await getEnvironmentDetails();
    const envArray = Array.isArray(response.data) ? response.data : [];
    setEnvs(envArray);
  } catch (err: any) {
    console.error('Error fetching environment details:', err);
    setError('Failed to load environment details');
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    loadData();
    if(!isReadEnabled){
      setError("You dont have permission to view Enviornment List")
    }
  }, []);

  const filteredEnvs = useMemo(() => {
    if (!searchText) return envs;
    return envs.filter(env =>
      Object.values(env).some(value =>
        value?.toString().toLowerCase().includes(searchText.toLowerCase())
      )
    );
  }, [envs, searchText]);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" sx={{ color: '#333' }}>
          Environment List
        </Typography>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={loadData}
          sx={{
            backgroundColor: '#734bd1',
            textTransform: 'none',
            '&:hover': { backgroundColor: '#5e3fb0' },
          }}
        >
          Refresh
        </Button>
      </Box>

      <Card>
        <CardContent>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress sx={{ color: '#734bd1' }} />
            </Box>
          ) : error ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <Typography color="error">{error}</Typography>
            </Box>
          ) : filteredEnvs.length === 0 ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <Typography>No environments found</Typography>
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 130, backgroundColor: '#e0d3ff' }}>Env Name</TableCell>
                    <TableCell sx={{ width: 180, backgroundColor: '#e0d3ff' }}>Description</TableCell>
                    <TableCell sx={{ width: 100, backgroundColor: '#e0d3ff' }}>Type</TableCell>
                    <TableCell sx={{ width: 120, backgroundColor: '#e0d3ff' }}>Display Name</TableCell>
                    <TableCell sx={{ width: 130, backgroundColor: '#e0d3ff' }}>Keyvault Name</TableCell>
                    <TableCell sx={{ width: 120, backgroundColor: '#e0d3ff' }} align="center">
                      
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredEnvs.map((env, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell>{env.Env_Nm}</TableCell>
                      <TableCell>{env.Env_Desc}</TableCell>
                      <TableCell>{env.Env_Typ}</TableCell>
                      <TableCell>{env.EnvDisplay_Nm}</TableCell>
                      <TableCell>{env.Keyvault_Nm}</TableCell>
                      <TableCell align="center">
                        <Button
                          variant="contained"
                          size="small"
                          sx={{
                            backgroundColor: '#734bd1',
                            textTransform: 'none',
                            '&:hover': { backgroundColor: '#5e3fb0' },
                          }}
                          onClick={() =>
                            navigate(`/EnvDetails/${env.Env_Nm}`, {
                              state: { envDetails: env },
                            })
                          }
                        >
                          Show More
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default EnvList;
